/**
 * Order money for the website — the SAME rule as the backend's App\Support\Money
 * and App\Services\Pricing\OrderPricingService (and the admin panel's
 * src/lib/money.ts), so the cart and checkout show exactly what the server bills
 * and Razorpay collects. Change one, change them all.
 *
 * ROUNDING (business, 9 Oct 2026 — supersedes the per-item whole-rupee rule):
 * every COMPONENT (line, GST, CGST / SGST / IGST, installation, shipping) is kept
 * to the PAISA; only the ORDER'S GRAND TOTAL — what Razorpay collects — goes to
 * the rupee, the difference shown as "Round off". The grand total's rule: paise
 * past the rupee compared with 50 — 50 or less drops (22.30 → 22), more than 50
 * rounds up (22.60 → 23).
 *
 * FORWARD FLOW: base (ex-GST) → discount off the base → taxable → GST added on
 * top → line total. Services (installation, measurement) are a base with the
 * service GST on top.
 */

/**
 * A COMPONENT, to the paisa — half a paisa up, on whole paise. The tiny nudge
 * keeps a value sitting exactly on half a paisa (held as …49999) from being read
 * as just under it: the backend rounds identically.
 */
export function roundMoney(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  const paise = Math.round(Math.abs(amount) * 100 + 1e-6);
  const value = paise / 100;
  return amount < 0 && value > 0 ? -value : value;
}

/** The ORDER'S GRAND TOTAL — what is collected — to the rupee: ≤ 50 paise drop, > 50 paise round up. */
export function roundTotal(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  const paise = Math.round(Math.abs(amount) * 100);
  const rupees = Math.floor(paise / 100);
  const rounded = rupees + (paise % 100 > 50 ? 1 : 0);
  return amount < 0 && rounded > 0 ? -rounded : rounded;
}

/** The Round off a summary prints: grand total − Σ components (signed, to the paisa). */
export function roundOff(grandTotal: number, componentsSum: number): number {
  return roundMoney(grandTotal - componentsSum);
}

/** A tax-EXCLUSIVE amount, taxed on top: base, tax = R(base × rate), total. */
export function exclusiveMoney(base: number, ratePct: number): { base: number; tax: number; total: number } {
  const b = roundMoney(Math.max(0, base));
  const tax = ratePct > 0 && b > 0 ? roundMoney((b * ratePct) / 100) : 0;
  return { base: b, tax, total: roundMoney(b + tax) };
}

/** One forward line: taxable = base − discount; GST = R(taxable × g); total = taxable + GST. */
export function forwardLine(baseExGst: number, discountExGst: number, gstPer: number): {
  base: number; discount: number; taxable: number; gst: number; total: number;
} {
  const base = roundMoney(Math.max(0, baseExGst));
  const discount = Math.min(roundMoney(Math.max(0, discountExGst)), base);
  const taxable = roundMoney(base - discount);
  const gst = gstPer > 0 && taxable > 0 ? roundMoney((taxable * gstPer) / 100) : 0;
  return { base, discount, taxable, gst, total: roundMoney(taxable + gst) };
}
